"use client";

import type { ComponentProps } from "react";
import { useMemo } from "react";
import { BuilderComponent, useIsPreviewing } from "@builder.io/react";
import { HubbleProvider } from "@hubble/web";
import { DynamicContainer } from "./style";
// import { normalizeBuilderSEOEntry } from "utils/builder.io";
import { mixpanelClient } from "analytics/mixpanel";
import { EVENTS } from "analytics/mixpanel/events";
import { GlobalStyle } from "../Dynamic/style";
import { captureException } from "observability";
import DefaultErrorPage from "next/error";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";

type BuilderPageProps = ComponentProps<typeof BuilderComponent>;

export const RenderBuilderContent = ({
  locale,
  content,
  data
}: BuilderPageProps) => {
  const isPreviewing = useIsPreviewing();
  const { isEU } = useCountryStateCookies();
  const localizedData = useMemo(() => {
    if (!data) {
      return null;
    }

    if (isEU) {
      // in the EU, we want to limit the symbols to only a few options:
      // https://gemini-spaceship.atlassian.net/browse/PUBSITE-621?atlOrigin=eyJpIjoiYTMzZDMxMDliZWJjNDJlY2JjMDEyODQ0OGM4N2VjNDgiLCJwIjoiaiJ9
      const updatedSymbols =
        data?.symbols?.filter((s: string) =>
          ["btcusdcperp", "ethusdcperp", "solusdcperp"].includes(
            s.toLowerCase()
          )
        ) ?? [];
      return {
        ...data,
        symbols: updatedSymbols,
        selectedToken: updatedSymbols[0]
      };
    }

    return {
      ...data,
      selectedToken: data?.symbols?.[0]
    };
  }, [isEU, data]);

  if (content || isPreviewing) {
    return (
      <HubbleProvider scheme={content?.data?.theme?.value} locale={{}}>
        {/* data-emotion-id="PageLayoutContainer" is required for hubbles responsive text feature. This allows us to access this feautre without having to deal with PageLayout's margins which are designed for the main app. */}
        <DynamicContainer data-emotion-id="PageLayoutContainer">
          {/* <Head
          {...normalizeBuilderSEOEntry(page?.data?.seoMetaInfo?.value?.data)}
        /> */}
          <GlobalStyle />
          <BuilderComponent
            model="page"
            locale={locale}
            content={content}
            data={localizedData}
            options={{ enrich: true }}
            contentError={(err) => captureException(err as Error)}
            context={{
              mpEvents: EVENTS,
              trackMixPanel: (
                eventName?: string,
                properties?: Record<string, unknown>
              ) => {
                mixpanelClient.track(
                  eventName ?? "RenderBuilderComponent error",
                  properties
                );
              }
            }}
          />
        </DynamicContainer>
      </HubbleProvider>
    );
  }
  return <DefaultErrorPage statusCode={404} />;
};
